import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail } from "../lib/api";
import { resolveAssetUrl } from "../lib/api";
import { useSettings } from "../lib/settings";
import { LogOut, Trash2, Plus, CheckCircle2, Edit2, X, Loader2, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const tabs = [
  { id: "sermons", label: "Sermons" },
  { id: "events", label: "Events" },
  { id: "pastors", label: "Pastors" },
  { id: "prayers", label: "Prayers" },
  { id: "visits", label: "Visits" },
  { id: "donations", label: "Donations" },
  { id: "settings", label: "Site Settings" },
];

const blankSermon = { title: "", speaker: "", scripture: "", date: "", youtube_url: "", description: "", series: "" };
const blankEvent = { title: "", date: "", time: "", location: "", description: "" };
const blankPastor = { name: "", role: "", bio: "", image_url: "", order: 0 };

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("sermons");

  useEffect(() => {
    if (ready && !user) nav("/admin/login");
  }, [user, ready, nav]);

  if (!ready) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[hsl(var(--linen))]">
      <header className="border-b border-[hsl(var(--line))] bg-[hsl(var(--sand))]">
        <div className="container-x py-6 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <Link to="/" className="display text-2xl text-[hsl(var(--ink))]">Sweet Embrace</Link>
            <span className="eyebrow">Admin</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-[hsl(var(--ink-muted))] hidden sm:inline">{user.email}</span>
            <button
              data-testid="admin-logout"
              onClick={async () => { await logout(); nav("/admin/login"); }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-[hsl(var(--line))] hover:bg-[hsl(var(--linen))] lift"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
        <div className="container-x flex flex-wrap gap-1 -mb-px">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm border-b-2 ${
                tab === t.id
                  ? "border-[hsl(var(--terracotta))] text-[hsl(var(--terracotta))]"
                  : "border-transparent text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink))]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="container-x py-10">
        {tab === "sermons" && <SermonsAdmin />}
        {tab === "events" && <EventsAdmin />}
        {tab === "pastors" && <PastorsAdmin />}
        {tab === "prayers" && <PrayersAdmin />}
        {tab === "visits" && <VisitsAdmin />}
        {tab === "donations" && <DonationsAdmin />}
        {tab === "settings" && <SettingsAdmin />}
      </main>
    </div>
  );
}

// ───── Sermons ────────────────────────────────────────────────
function SermonsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankSermon);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/sermons").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) await api.put(`/sermons/${editing}`, form);
      else await api.post("/sermons", form);
      setForm(blankSermon); setEditing(null); await load();
      toast.success(editing ? "Sermon updated" : "Sermon added");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this sermon?")) return;
    await api.delete(`/sermons/${id}`); load(); toast.success("Deleted");
  };

  return (
    <CrudShell
      title={editing ? "Edit sermon" : "New sermon"}
      onCancel={editing ? () => { setEditing(null); setForm(blankSermon); } : null}
      form={
        <form onSubmit={submit} data-testid="sermon-form" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Title" testid="sermon-input-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <Input label="Speaker" testid="sermon-input-speaker" value={form.speaker} onChange={(v) => setForm({ ...form, speaker: v })} />
          <Input label="Scripture" testid="sermon-input-scripture" value={form.scripture} onChange={(v) => setForm({ ...form, scripture: v })} />
          <Input label="Series" testid="sermon-input-series" value={form.series} onChange={(v) => setForm({ ...form, series: v })} />
          <Input label="Date (YYYY-MM-DD)" testid="sermon-input-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
          <Input label="YouTube URL" testid="sermon-input-youtube" value={form.youtube_url} onChange={(v) => setForm({ ...form, youtube_url: v })} />
          <Textarea label="Description" testid="sermon-input-desc" value={form.description} onChange={(v) => setForm({ ...form, description: v })} className="md:col-span-2" />
          <Submit busy={busy} testid="sermon-submit" label={editing ? "Save changes" : "Add sermon"} />
        </form>
      }
      list={
        <div className="divide-y divide-[hsl(var(--line))] border-y border-[hsl(var(--line))]">
          {items.map((s) => (
            <div key={s.id} className="py-4 flex items-start justify-between gap-4">
              <div>
                <div className="display text-xl">{s.title}</div>
                <div className="text-xs text-[hsl(var(--ink-muted))] mt-1">{s.speaker} · {s.date}</div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`sermon-edit-${s.id}`} onClick={() => { setEditing(s.id); setForm({ ...blankSermon, ...s }); }} className="p-2 hover:text-[hsl(var(--terracotta))]"><Edit2 size={14} /></button>
                <button data-testid={`sermon-delete-${s.id}`} onClick={() => remove(s.id)} className="p-2 hover:text-[hsl(var(--destructive))]"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      }
    />
  );
}

// ───── Events ────────────────────────────────────────────────
function EventsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankEvent);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/events").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editing) await api.put(`/events/${editing}`, form);
      else await api.post("/events", form);
      setForm(blankEvent); setEditing(null); load();
      toast.success(editing ? "Event updated" : "Event added");
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/events/${id}`); load(); toast.success("Deleted");
  };

  return (
    <CrudShell
      title={editing ? "Edit event" : "New event"}
      onCancel={editing ? () => { setEditing(null); setForm(blankEvent); } : null}
      form={
        <form onSubmit={submit} data-testid="event-form" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Title" testid="event-input-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <Input label="Date (YYYY-MM-DD)" testid="event-input-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
          <Input label="Time" testid="event-input-time" value={form.time} onChange={(v) => setForm({ ...form, time: v })} />
          <Input label="Location" testid="event-input-location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} />
          <Textarea label="Description" testid="event-input-desc" value={form.description} onChange={(v) => setForm({ ...form, description: v })} className="md:col-span-2" />
          <Submit busy={busy} testid="event-submit" label={editing ? "Save changes" : "Add event"} />
        </form>
      }
      list={
        <div className="divide-y divide-[hsl(var(--line))] border-y border-[hsl(var(--line))]">
          {items.map((s) => (
            <div key={s.id} className="py-4 flex items-start justify-between gap-4">
              <div>
                <div className="display text-xl">{s.title}</div>
                <div className="text-xs text-[hsl(var(--ink-muted))] mt-1">{s.date} · {s.time} · {s.location}</div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`event-edit-${s.id}`} onClick={() => { setEditing(s.id); setForm({ ...blankEvent, ...s }); }} className="p-2 hover:text-[hsl(var(--terracotta))]"><Edit2 size={14} /></button>
                <button data-testid={`event-delete-${s.id}`} onClick={() => remove(s.id)} className="p-2 hover:text-[hsl(var(--destructive))]"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      }
    />
  );
}

// ───── Pastors ───────────────────────────────────────────────
function PastorsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankPastor);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/pastors").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = { ...form, order: Number(form.order) || 0 };
      if (editing) await api.put(`/pastors/${editing}`, body);
      else await api.post("/pastors", body);
      setForm(blankPastor); setEditing(null); load();
      toast.success(editing ? "Pastor updated" : "Pastor added");
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this pastor?")) return;
    await api.delete(`/pastors/${id}`); load(); toast.success("Deleted");
  };

  return (
    <CrudShell
      title={editing ? "Edit pastor" : "New pastor"}
      onCancel={editing ? () => { setEditing(null); setForm(blankPastor); } : null}
      form={
        <form onSubmit={submit} data-testid="pastor-form" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Name" testid="pastor-input-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Input label="Role" testid="pastor-input-role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
          <div className="md:col-span-2">
            <ImageUploadField
              label="Photo"
              testidPrefix="pastor"
              value={form.image_url}
              onChange={(url) => setForm({ ...form, image_url: url })}
            />
          </div>
          <Input label="Display order" testid="pastor-input-order" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
          <Textarea label="Bio" testid="pastor-input-bio" value={form.bio} onChange={(v) => setForm({ ...form, bio: v })} className="md:col-span-2" />
          <Submit busy={busy} testid="pastor-submit" label={editing ? "Save changes" : "Add pastor"} />
        </form>
      }
      list={
        <div className="divide-y divide-[hsl(var(--line))] border-y border-[hsl(var(--line))]">
          {items.map((s) => (
            <div key={s.id} className="py-4 flex items-start justify-between gap-4">
              <div>
                <div className="display text-xl">{s.name}</div>
                <div className="text-xs text-[hsl(var(--ink-muted))] mt-1">{s.role}</div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`pastor-edit-${s.id}`} onClick={() => { setEditing(s.id); setForm({ ...blankPastor, ...s }); }} className="p-2 hover:text-[hsl(var(--terracotta))]"><Edit2 size={14} /></button>
                <button data-testid={`pastor-delete-${s.id}`} onClick={() => remove(s.id)} className="p-2 hover:text-[hsl(var(--destructive))]"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      }
    />
  );
}

// ───── Prayers ───────────────────────────────────────────────
function PrayersAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/prayers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const approve = async (id) => { await api.post(`/prayers/${id}/approve`); load(); toast.success("Approved"); };
  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    await api.delete(`/prayers/${id}`); load(); toast.success("Deleted");
  };

  return (
    <div className="grid grid-cols-1 gap-3">
      <h2 className="display text-3xl mb-2">All prayer requests</h2>
      <div className="divide-y divide-[hsl(var(--line))] border-y border-[hsl(var(--line))]">
        {items.map((p) => (
          <div key={p.id} data-testid={`prayer-admin-${p.id}`} className="py-5 flex items-start justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">{p.name}</span>
                <span className={`text-[10px] px-2 py-0.5 uppercase tracking-[0.18em] ${p.is_public ? "bg-[hsl(var(--terracotta))]/15 text-[hsl(var(--terracotta))]" : "bg-[hsl(var(--moss))]/15 text-[hsl(var(--moss))]"}`}>
                  {p.is_public ? "Public" : "Private"}
                </span>
                {p.is_public && (
                  <span className={`text-[10px] px-2 py-0.5 uppercase tracking-[0.18em] ${p.is_approved ? "bg-[hsl(var(--moss))]/15 text-[hsl(var(--moss))]" : "bg-[hsl(var(--ink-muted))]/15 text-[hsl(var(--ink-muted))]"}`}>
                    {p.is_approved ? "Approved" : "Pending"}
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-[hsl(var(--ink))]">{p.message}</p>
              <div className="mt-2 text-xs text-[hsl(var(--ink-muted))]">
                {new Date(p.created_at).toLocaleString()} {p.email && <>· {p.email}</>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {p.is_public && !p.is_approved && (
                <button data-testid={`prayer-approve-${p.id}`} onClick={() => approve(p.id)} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-[hsl(var(--moss))] text-[hsl(var(--moss))] rounded-sm">
                  <CheckCircle2 size={14} /> Approve
                </button>
              )}
              <button data-testid={`prayer-delete-${p.id}`} onClick={() => remove(p.id)} className="p-2 hover:text-[hsl(var(--destructive))]"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {!items.length && <div className="py-6 text-sm text-[hsl(var(--ink-muted))]">No prayer requests yet.</div>}
      </div>
    </div>
  );
}

// ───── Visits (Plan Your Visit submissions) ─────────────────
function VisitsAdmin() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => api.get("/visits").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/visits/${id}`, { status });
    load(); toast.success("Updated");
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this visit plan?")) return;
    await api.delete(`/visits/${id}`); setActive(null); load(); toast.success("Deleted");
  };

  const statusPill = (s) => {
    const map = {
      new: "bg-[hsl(var(--terracotta))]/15 text-[hsl(var(--terracotta))]",
      followed_up: "bg-[hsl(var(--moss))]/15 text-[hsl(var(--moss))]",
      visited: "bg-[hsl(var(--ink))]/10 text-[hsl(var(--ink))]",
    };
    return <span className={`text-[10px] px-2 py-0.5 uppercase tracking-[0.18em] ${map[s] || ""}`}>{s.replace("_", " ")}</span>;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-7">
        <div className="flex items-center justify-between mb-4">
          <h2 className="display text-3xl">Plan-a-visit requests</h2>
          <span className="text-xs text-[hsl(var(--ink-muted))]">{items.length} total</span>
        </div>
        <div className="divide-y divide-[hsl(var(--line))] border-y border-[hsl(var(--line))]">
          {items.map((v) => (
            <button
              key={v.id}
              data-testid={`visit-row-${v.id}`}
              onClick={() => setActive(v)}
              className={`w-full text-left py-4 flex items-start justify-between gap-4 lift ${active?.id === v.id ? "bg-[hsl(var(--sand))] px-3" : "px-1"}`}
            >
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-medium">{v.name}</span>
                  {statusPill(v.status)}
                </div>
                <div className="text-xs text-[hsl(var(--ink-muted))] mt-1">
                  {v.email} · party of {v.party_adults + v.party_kids}
                  {v.planned_date && <> · {new Date(v.planned_date).toLocaleDateString()}</>}
                  {v.service_time && <> · {v.service_time}</>}
                </div>
              </div>
              <div className="text-xs text-[hsl(var(--ink-muted))] whitespace-nowrap">
                {new Date(v.created_at).toLocaleDateString()}
              </div>
            </button>
          ))}
          {!items.length && <div className="py-6 text-sm text-[hsl(var(--ink-muted))]">No visit plans yet.</div>}
        </div>
      </div>

      <div className="lg:col-span-5">
        {active ? (
          <div data-testid="visit-detail" className="border border-[hsl(var(--line))] bg-[hsl(var(--sand))] p-6 rounded-sm sticky top-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="display text-2xl">{active.name}</div>
                <div className="text-xs text-[hsl(var(--ink-muted))] mt-1">{active.email}{active.phone && <> · {active.phone}</>}</div>
              </div>
              {statusPill(active.status)}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
              <Stat label="Adults" value={active.party_adults} />
              <Stat label="Kids" value={active.party_kids} />
              <Stat label="Kids' ages" value={active.kids_ages || "—"} />
              <Stat label="Heard from" value={active.heard_from || "—"} />
              <Stat label="Planned date" value={active.planned_date || "—"} />
              <Stat label="Service" value={active.service_time || "—"} />
            </div>

            {active.notes && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">Notes</div>
                <p className="mt-2 text-sm leading-relaxed">{active.notes}</p>
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-2">
              <button data-testid="visit-mark-followed" onClick={() => setStatus(active.id, "followed_up")} className="px-3 py-2 text-xs border border-[hsl(var(--moss))] text-[hsl(var(--moss))] rounded-sm lift">Mark followed-up</button>
              <button data-testid="visit-mark-visited" onClick={() => setStatus(active.id, "visited")} className="px-3 py-2 text-xs border border-[hsl(var(--ink))] text-[hsl(var(--ink))] rounded-sm lift">Mark visited</button>
              <button data-testid="visit-mark-new" onClick={() => setStatus(active.id, "new")} className="px-3 py-2 text-xs border border-[hsl(var(--line))] text-[hsl(var(--ink-muted))] rounded-sm lift">Reset to new</button>
              <button data-testid="visit-delete" onClick={() => remove(active.id)} className="ml-auto px-3 py-2 text-xs text-[hsl(var(--destructive))] rounded-sm flex items-center gap-1"><Trash2 size={12} /> Delete</button>
            </div>

            <a
              href={`mailto:${active.email}?subject=Welcome to Sweet Embrace Church&body=Hi ${active.name.split(" ")[0]},%0D%0A%0D%0AWe're so glad you're planning to visit us`}
              data-testid="visit-email-reply"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 px-5 py-3 rounded-sm bg-[hsl(var(--terracotta))] text-[hsl(var(--linen))] text-sm lift"
            >
              Reply via email
            </a>
          </div>
        ) : (
          <div className="border border-dashed border-[hsl(var(--line))] p-10 text-sm text-[hsl(var(--ink-muted))] text-center rounded-sm">
            Select a request to see details.
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">{label}</div>
      <div className="mt-1 text-[hsl(var(--ink))]">{value}</div>
    </div>
  );
}

// ───── Donations (read-only) ─────────────────────────────────
function DonationsAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/donations/recent").then((r) => setItems(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <h2 className="display text-3xl mb-6">Recent donations</h2>
      <div className="overflow-x-auto border border-[hsl(var(--line))] rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--sand))]">
            <tr>
              {["Date", "Donor", "Email", "Amount", "Status", "Note"].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-medium text-[hsl(var(--ink-muted))] uppercase text-[10px] tracking-[0.18em]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(var(--line))]">
            {items.map((t) => (
              <tr key={t.session_id} data-testid={`donation-row-${t.session_id}`}>
                <td className="px-4 py-3 text-[hsl(var(--ink-muted))]">{new Date(t.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">{t.metadata?.donor_name || "Anonymous"}</td>
                <td className="px-4 py-3 text-[hsl(var(--ink-muted))]">{t.metadata?.donor_email || "—"}</td>
                <td className="px-4 py-3">${Number(t.amount).toFixed(2)}</td>
                <td className="px-4 py-3 capitalize">{t.payment_status}</td>
                <td className="px-4 py-3 text-[hsl(var(--ink-muted))]">{t.metadata?.note || "—"}</td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={6} className="px-4 py-6 text-[hsl(var(--ink-muted))]">No donations recorded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───── Site Settings ─────────────────────────────────────────
function SettingsAdmin() {
  const { settings, reload } = useSettings();
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/settings", form);
      await reload();
      toast.success("Site settings saved");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="settings-form" className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="display text-3xl">Site Settings</h2>
        <button
          type="submit"
          data-testid="settings-save"
          disabled={busy}
          className="lift inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta-deep))] text-[hsl(var(--linen))] text-sm tracking-wide disabled:opacity-60"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          Save changes
        </button>
      </div>

      <SettingsSection title="Identity">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Church name" testid="settings-church-name" value={form.church_name} onChange={(v) => set("church_name", v)} />
          <Input label="Short name (used in header)" testid="settings-short-name" value={form.short_name} onChange={(v) => set("short_name", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="Homepage hero">
        <div className="grid grid-cols-1 gap-4">
          <Input label="Eyebrow / tagline" testid="settings-tagline" value={form.tagline} onChange={(v) => set("tagline", v)} />
          <Input label="Headline" testid="settings-hero-headline" value={form.hero_headline} onChange={(v) => set("hero_headline", v)} />
          <Textarea label="Subhead" testid="settings-hero-subhead" value={form.hero_subhead} onChange={(v) => set("hero_subhead", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="Service times">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Sunday service 1" testid="settings-sunday-1" value={form.sunday_service_1} onChange={(v) => set("sunday_service_1", v)} />
          <Input label="Sunday service 2" testid="settings-sunday-2" value={form.sunday_service_2} onChange={(v) => set("sunday_service_2", v)} />
          <Input label="Mid-week label" testid="settings-midweek-label" value={form.midweek_label} onChange={(v) => set("midweek_label", v)} />
          <Input label="Mid-week day & time" testid="settings-midweek-time" value={form.midweek_time} onChange={(v) => set("midweek_time", v)} />
          <Input label="Mid-week location" testid="settings-midweek-location" value={form.midweek_location} onChange={(v) => set("midweek_location", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="Contact">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Address line 1" testid="settings-addr1" value={form.address_line1} onChange={(v) => set("address_line1", v)} />
          <Input label="Address line 2 (city, state, zip)" testid="settings-addr2" value={form.address_line2} onChange={(v) => set("address_line2", v)} />
          <Input label="Phone" testid="settings-phone" value={form.phone} onChange={(v) => set("phone", v)} />
          <Input label="Email" testid="settings-email" value={form.email} onChange={(v) => set("email", v)} />
          <Input label="Office hours" testid="settings-office-hours" value={form.office_hours} onChange={(v) => set("office_hours", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="Footer & quote">
        <div className="grid grid-cols-1 gap-4">
          <Textarea label="Footer blurb" testid="settings-footer-blurb" value={form.footer_blurb} onChange={(v) => set("footer_blurb", v)} />
          <Textarea label="Featured quote" testid="settings-quote" value={form.quote} onChange={(v) => set("quote", v)} />
          <Input label="Quote attribution" testid="settings-quote-attr" value={form.quote_attribution} onChange={(v) => set("quote_attribution", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="Home page — images">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ImageUploadField label="Hero background (top of homepage)" testidPrefix="settings-home-hero" value={form.home_hero_image_url} onChange={(v) => set("home_hero_image_url", v)} />
          <ImageUploadField label="Community banner (quote background)" testidPrefix="settings-home-community" value={form.home_community_image_url} onChange={(v) => set("home_community_image_url", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="Home page — latest sermon block">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Eyebrow" testid="settings-home-sermon-eyebrow" value={form.home_sermon_eyebrow} onChange={(v) => set("home_sermon_eyebrow", v)} />
          <Input label="Link text" testid="settings-home-sermon-cta" value={form.home_sermon_cta} onChange={(v) => set("home_sermon_cta", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="Home page — Three doors block">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Input label="Eyebrow" testid="settings-home-doors-eyebrow" value={form.home_doors_eyebrow} onChange={(v) => set("home_doors_eyebrow", v)} />
          <Input label="Headline" testid="settings-home-doors-headline" value={form.home_doors_headline} onChange={(v) => set("home_doors_headline", v)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { i: "prayer", label: "Prayer tile" },
            { i: "give", label: "Give tile" },
            { i: "about", label: "About tile" },
          ].map(({ i, label }) => (
            <div key={i} className="border border-[hsl(var(--line))] bg-[hsl(var(--linen))] p-4 rounded-sm">
              <div className="eyebrow text-xs mb-3">{label}</div>
              <Input label="Title" testid={`settings-home-tile-${i}-title`} value={form[`home_tile_${i}_title`]} onChange={(v) => set(`home_tile_${i}_title`, v)} />
              <div className="mt-3">
                <Textarea label="Description" testid={`settings-home-tile-${i}-text`} value={form[`home_tile_${i}_text`]} onChange={(v) => set(`home_tile_${i}_text`, v)} />
              </div>
            </div>
          ))}
        </div>
      </SettingsSection>

      <SettingsSection title="Home page — events block">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input label="Eyebrow" testid="settings-home-events-eyebrow" value={form.home_events_eyebrow} onChange={(v) => set("home_events_eyebrow", v)} />
          <Input label="Headline" testid="settings-home-events-headline" value={form.home_events_headline} onChange={(v) => set("home_events_headline", v)} />
          <Input label="Link text" testid="settings-home-events-cta" value={form.home_events_cta} onChange={(v) => set("home_events_cta", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="Sermons page">
        <ImageUploadField label="Hero background" testidPrefix="settings-sermons-hero" value={form.sermons_hero_image_url} onChange={(v) => set("sermons_hero_image_url", v)} />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Eyebrow" testid="settings-sermons-eyebrow" value={form.sermons_eyebrow} onChange={(v) => set("sermons_eyebrow", v)} />
          <Input label="Headline" testid="settings-sermons-headline" value={form.sermons_headline} onChange={(v) => set("sermons_headline", v)} />
        </div>
        <div className="mt-4">
          <Textarea label="Intro paragraph" testid="settings-sermons-intro" value={form.sermons_intro} onChange={(v) => set("sermons_intro", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="Prayer page">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Form eyebrow" testid="settings-prayer-eyebrow" value={form.prayer_eyebrow} onChange={(v) => set("prayer_eyebrow", v)} />
          <Input label="Form headline" testid="settings-prayer-headline" value={form.prayer_headline} onChange={(v) => set("prayer_headline", v)} />
        </div>
        <div className="mt-4">
          <Textarea label="Form intro paragraph" testid="settings-prayer-intro" value={form.prayer_intro} onChange={(v) => set("prayer_intro", v)} />
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Wall eyebrow" testid="settings-prayer-wall-eyebrow" value={form.prayer_wall_eyebrow} onChange={(v) => set("prayer_wall_eyebrow", v)} />
          <Input label="Wall headline" testid="settings-prayer-wall-headline" value={form.prayer_wall_headline} onChange={(v) => set("prayer_wall_headline", v)} />
        </div>
        <div className="mt-4">
          <Textarea label="Wall intro" testid="settings-prayer-wall-intro" value={form.prayer_wall_intro} onChange={(v) => set("prayer_wall_intro", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="Give page">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Eyebrow" testid="settings-give-eyebrow" value={form.give_eyebrow} onChange={(v) => set("give_eyebrow", v)} />
          <Input label="Headline" testid="settings-give-headline" value={form.give_headline} onChange={(v) => set("give_headline", v)} />
        </div>
        <div className="mt-4">
          <Textarea label="Intro paragraph" testid="settings-give-intro" value={form.give_intro} onChange={(v) => set("give_intro", v)} />
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="border border-[hsl(var(--line))] bg-[hsl(var(--linen))] p-4 rounded-sm">
              <div className="eyebrow text-xs mb-3">Fund {i}</div>
              <Input label="Title" testid={`settings-give-fund-${i}-title`} value={form[`give_fund_${i}_title`]} onChange={(v) => set(`give_fund_${i}_title`, v)} />
              <div className="mt-3">
                <Textarea label="Description" testid={`settings-give-fund-${i}-text`} value={form[`give_fund_${i}_text`]} onChange={(v) => set(`give_fund_${i}_text`, v)} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6">
          <Textarea label="Disclaimer / tax note (small print)" testid="settings-give-disclaimer" value={form.give_disclaimer} onChange={(v) => set("give_disclaimer", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="Events page">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Eyebrow" testid="settings-events-eyebrow" value={form.events_eyebrow} onChange={(v) => set("events_eyebrow", v)} />
          <Input label="Headline" testid="settings-events-headline" value={form.events_headline} onChange={(v) => set("events_headline", v)} />
        </div>
        <div className="mt-4">
          <Textarea label="Intro paragraph" testid="settings-events-intro" value={form.events_intro} onChange={(v) => set("events_intro", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="Contact page">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Eyebrow" testid="settings-contact-eyebrow" value={form.contact_eyebrow} onChange={(v) => set("contact_eyebrow", v)} />
          <Input label="Headline" testid="settings-contact-headline" value={form.contact_headline} onChange={(v) => set("contact_headline", v)} />
        </div>
        <div className="mt-4">
          <Textarea label="Intro paragraph" testid="settings-contact-intro" value={form.contact_intro} onChange={(v) => set("contact_intro", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="Plan-a-visit page">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Eyebrow" testid="settings-visit-eyebrow" value={form.visit_eyebrow} onChange={(v) => set("visit_eyebrow", v)} />
          <Input label="Headline" testid="settings-visit-headline" value={form.visit_headline} onChange={(v) => set("visit_headline", v)} />
        </div>
        <div className="mt-4">
          <Textarea label="Intro paragraph" testid="settings-visit-intro" value={form.visit_intro} onChange={(v) => set("visit_intro", v)} />
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="border border-[hsl(var(--line))] bg-[hsl(var(--linen))] p-4 rounded-sm">
              <div className="eyebrow text-xs mb-3">Perk {i}</div>
              <Input label="Title" testid={`settings-visit-perk-${i}-title`} value={form[`visit_perk_${i}_title`]} onChange={(v) => set(`visit_perk_${i}_title`, v)} />
              <div className="mt-3">
                <Textarea label="Description" testid={`settings-visit-perk-${i}-text`} value={form[`visit_perk_${i}_text`]} onChange={(v) => set(`visit_perk_${i}_text`, v)} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6">
          <ImageUploadField label="Sidebar image" testidPrefix="settings-visit-image" value={form.visit_image_url} onChange={(v) => set("visit_image_url", v)} />
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Submit button label" testid="settings-visit-submit-label" value={form.visit_submit_label} onChange={(v) => set("visit_submit_label", v)} />
          <Input label="Disclaimer under submit button" testid="settings-visit-form-disclaimer" value={form.visit_form_disclaimer} onChange={(v) => set("visit_form_disclaimer", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="About page — hero">
        <div className="grid grid-cols-1 gap-4">
          <Input label="Eyebrow" testid="settings-about-eyebrow" value={form.about_eyebrow} onChange={(v) => set("about_eyebrow", v)} />
          <Input label="Headline" testid="settings-about-headline" value={form.about_headline} onChange={(v) => set("about_headline", v)} />
          <Textarea label="Intro paragraph 1" testid="settings-about-p1" value={form.about_intro_p1} onChange={(v) => set("about_intro_p1", v)} />
          <Textarea label="Intro paragraph 2" testid="settings-about-p2" value={form.about_intro_p2} onChange={(v) => set("about_intro_p2", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="About page — beliefs / rhythms">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Input label="Section eyebrow" testid="settings-beliefs-eyebrow" value={form.beliefs_eyebrow} onChange={(v) => set("beliefs_eyebrow", v)} />
          <Input label="Section headline" testid="settings-beliefs-headline" value={form.beliefs_headline} onChange={(v) => set("beliefs_headline", v)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="border border-[hsl(var(--line))] bg-[hsl(var(--linen))] p-4 rounded-sm">
              <div className="eyebrow text-xs mb-3">Rhythm {String(i).padStart(2, "0")}</div>
              <Input label="Title" testid={`settings-belief-${i}-title`} value={form[`belief_${i}_title`]} onChange={(v) => set(`belief_${i}_title`, v)} />
              <div className="mt-3">
                <Textarea label="Description" testid={`settings-belief-${i}-text`} value={form[`belief_${i}_text`]} onChange={(v) => set(`belief_${i}_text`, v)} />
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-[hsl(var(--ink-muted))] mt-4">Clear both fields on a rhythm to hide it. Clear all four to hide the entire section.</p>
      </SettingsSection>

      <SettingsSection title="About page — leadership intro">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Eyebrow" testid="settings-pastors-eyebrow" value={form.pastors_eyebrow} onChange={(v) => set("pastors_eyebrow", v)} />
          <Input label="Headline" testid="settings-pastors-headline" value={form.pastors_headline} onChange={(v) => set("pastors_headline", v)} />
        </div>
      </SettingsSection>

      <SettingsSection title="Social links">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input label="Instagram URL" testid="settings-instagram" value={form.instagram_url} onChange={(v) => set("instagram_url", v)} />
          <Input label="Facebook URL" testid="settings-facebook" value={form.facebook_url} onChange={(v) => set("facebook_url", v)} />
          <Input label="YouTube URL" testid="settings-youtube" value={form.youtube_url} onChange={(v) => set("youtube_url", v)} />
        </div>
      </SettingsSection>

      <div className="mt-8 flex justify-end">
        <button
          type="submit"
          data-testid="settings-save-bottom"
          disabled={busy}
          className="lift inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta-deep))] text-[hsl(var(--linen))] text-sm tracking-wide disabled:opacity-60"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          Save changes
        </button>
      </div>
    </form>
  );
}

function SettingsSection({ title, children }) {
  return (
    <div className="mb-10">
      <div className="eyebrow mb-4">{title}</div>
      <div className="border border-[hsl(var(--line))] bg-[hsl(var(--sand))] p-6 rounded-sm">{children}</div>
    </div>
  );
}

// ───── Shared bits ───────────────────────────────────────────
function CrudShell({ title, form, list, onCancel }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="display text-2xl">{title}</h2>
          {onCancel && (
            <button onClick={onCancel} className="text-xs flex items-center gap-1 text-[hsl(var(--ink-muted))]"><X size={12} /> Cancel</button>
          )}
        </div>
        <div className="border border-[hsl(var(--line))] bg-[hsl(var(--sand))] p-6 rounded-sm">{form}</div>
      </div>
      <div className="lg:col-span-7">{list}</div>
    </div>
  );
}

function Input({ label, testid, value, onChange }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">{label}</span>
      <input
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-[hsl(var(--linen))] border border-[hsl(var(--line))] px-3 py-2.5 rounded-sm focus:outline-none focus:border-[hsl(var(--terracotta))]"
      />
    </label>
  );
}
function Textarea({ label, testid, value, onChange, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">{label}</span>
      <textarea
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="mt-2 w-full bg-[hsl(var(--linen))] border border-[hsl(var(--line))] px-3 py-2.5 rounded-sm focus:outline-none focus:border-[hsl(var(--terracotta))]"
      />
    </label>
  );
}
function Submit({ busy, testid, label }) {
  return (
    <div className="md:col-span-2">
      <button
        data-testid={testid}
        disabled={busy}
        className="lift inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta-deep))] text-[hsl(var(--linen))] text-sm tracking-wide disabled:opacity-60"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
        {label}
      </button>
    </div>
  );
}

function ImageUploadField({ label, testidPrefix, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Photo uploaded");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  return (
    <div>
      <div className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">{label}</div>
      <div className="mt-2 flex items-start gap-4">
        <div className="w-24 h-24 shrink-0 border border-[hsl(var(--line))] bg-[hsl(var(--linen))] overflow-hidden flex items-center justify-center">
          {value ? (
            <img src={resolveAssetUrl(value)} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={20} className="text-[hsl(var(--ink-muted))]" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onFile}
            data-testid={`${testidPrefix}-upload-input`}
            className="hidden"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`${testidPrefix}-upload-btn`}
              className="lift inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[hsl(var(--ink))] text-[hsl(var(--linen))] text-xs disabled:opacity-60"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {uploading ? "Uploading…" : value ? "Replace photo" : "Upload photo"}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                data-testid={`${testidPrefix}-upload-clear`}
                className="px-3 py-2 text-xs text-[hsl(var(--ink-muted))] underline"
              >
                Remove
              </button>
            )}
          </div>
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`${testidPrefix}-input-image`}
            placeholder="…or paste an image URL"
            className="w-full bg-[hsl(var(--linen))] border border-[hsl(var(--line))] px-3 py-2 rounded-sm text-xs focus:outline-none focus:border-[hsl(var(--terracotta))]"
          />
          <p className="text-[10px] text-[hsl(var(--ink-muted))]">JPG, PNG, WEBP or GIF · up to 5 MB</p>
        </div>
      </div>
    </div>
  );
}
